import React from 'react';

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helpText?: string;
  error?: string;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, helpText, error, className = '', ...props }, ref) => {
    return (
      <div className="space-y-1">
        {label && (
          <label className="text-sm font-medium text-[var(--text-sec)]">
            {label}
            {props.required && <span className="text-[var(--coral)] ml-1">*</span>}
          </label>
        )}
        <input
          ref={ref}
          className={`w-full px-3 py-2 border  text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)] ${
            error ? 'border-[var(--coral)] focus:ring-red-500' : 'border-[var(--rule)]'
          } ${className}`}
          {...props}
        />
        {helpText && !error && (
          <p className="text-xs text-[var(--text-ter)]">{helpText}</p>
        )}
        {error && (
          <p className="text-xs text-[var(--coral)]">{error}</p>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helpText?: string;
  error?: string;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, helpText, error, className = '', ...props }, ref) => {
    return (
      <div className="space-y-1">
        {label && (
          <label className="text-sm font-medium text-[var(--text-sec)]">
            {label}
            {props.required && <span className="text-[var(--coral)] ml-1">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          className={`w-full px-3 py-2 border  text-sm resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)] ${
            error ? 'border-[var(--coral)] focus:ring-red-500' : 'border-[var(--rule)]'
          } ${className}`}
          {...props}
        />
        {helpText && !error && (
          <p className="text-xs text-[var(--text-ter)]">{helpText}</p>
        )}
        {error && (
          <p className="text-xs text-[var(--coral)]">{error}</p>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helpText?: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, helpText, className = '', ...props }, ref) => {
    return (
      <div className="space-y-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            ref={ref}
            type="checkbox"
            className={`w-4 h-4 text-[var(--text-ter)] border-[var(--rule)] rounded focus:ring-blue-500 ${className}`}
            {...props}
          />
          {label && (
            <span className="text-sm font-medium text-[var(--text-sec)]">
              {label}
              {props.required && <span className="text-[var(--coral)] ml-1">*</span>}
            </span>
          )}
        </label>
        {helpText && (
          <p className="text-xs text-[var(--text-ter)] ml-6">{helpText}</p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

interface RadioGroupProps {
  label?: string;
  options: { value: string; label: string }[];
  value?: string;
  onChange?: (value: string) => void;
  helpText?: string;
  required?: boolean;
}

export function RadioGroup({ label, options, value, onChange, helpText, required }: RadioGroupProps) {
  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-[var(--text-sec)]">
          {label}
          {required && <span className="text-[var(--coral)] ml-1">*</span>}
        </label>
      )}
      <div className="flex gap-4">
        {options.map((option) => (
          <label key={option.value} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange?.(e.target.value)}
              className="w-4 h-4 text-[var(--text-ter)] border-[var(--rule)] focus:ring-blue-500"
            />
            <span className="text-sm text-[var(--text-sec)]">{option.label}</span>
          </label>
        ))}
      </div>
      {helpText && (
        <p className="text-xs text-[var(--text-ter)]">{helpText}</p>
      )}
    </div>
  );
}






